import type {
  ProfileTemplateFieldMode,
  ProfileTemplateFieldPoliciesDto,
} from '@up2stay/up2promo-api-client';
import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@up2stay/up2promo-ui';
import type { FieldError } from 'react-hook-form';

import { UpgradeCard } from '@/core/components/billing/upgrade-surfaces';
import { ProfileAccentColorField } from '@/core/components/profile/profile-accent-color-field';
import {
  ProfileAppearanceFields,
  type ProfileAppearanceDict,
} from '@/core/components/profile/profile-appearance-fields';
import { TemplateFieldRow } from '@/core/components/profile-templates/template-field-row';
import type { TemplateMemberValuesEditorUiProps } from '@/core/components/profile-templates/template-policy-member-controls';
import {
  TEMPLATE_DETAIL_MUTED_CLASS,
  TEMPLATE_DETAIL_SECTION_TITLE_CLASS,
  TemplateDetailSectionCard,
} from '@/core/components/profile-templates/template-detail-section-card';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import type { ProfileTemplateFieldKey } from '@/core/lib/profile-template-fields';
import type { TemplateFormData } from '@/core/lib/profile-template-form';
import { PROFILE_STYLING_FORM_DEFAULTS } from '@/core/lib/profile-styling-form';

export type TemplateDetailSettingsDict = {
  title: string;
  description: string;
  language: string;
  languagePlaceholder: string;
  languageAuto: string;
  languageGerman: string;
  languageEnglish: string;
  visibility: string;
  visibilityPlaceholder: string;
  visibilityUnlisted: string;
  visibilityPrivate: string;
  showUp2promoBranding: string;
  showUp2promoBrandingDescription: string;
  showUp2promoBrandingUpgradeTitle: string;
  showUp2promoBrandingUpgradeDescription: string;
  accentColor: string;
  accentColorDescription: string;
  accentColorReset: string;
  accentColorPlaceholder: string;
  accentColorCtaPreview: string;
  accentColorCtaAdjustedDarker: string;
  accentColorCtaAdjustedLighter: string;
  appearance: ProfileAppearanceDict;
};

export type TemplateDetailSettingsFieldLabels = {
  language: string;
  visibility: string;
  showUp2promoBranding: string;
  layout?: string;
};

type TemplateDetailSettingsSectionProps = {
  dict: TemplateDetailSettingsDict;
  fieldLabels: TemplateDetailSettingsFieldLabels;
  policies: ProfileTemplateFieldPoliciesDto;
  onPolicyChange: (
    key: ProfileTemplateFieldKey,
    mode: ProfileTemplateFieldMode,
  ) => void;
  canEditTemplate: boolean;
  policyLabels: { locked: string; free: string };
  memberValueProps: TemplateMemberValuesEditorUiProps;
  /** Sample CTA label for the accent preview (public profile strings). */
  accentCtaLabel: string;
  watchedForm: TemplateFormData;
  watchedLanguage: TemplateFormData['language'];
  watchedVisibility: TemplateFormData['visibility'];
  languageError?: FieldError;
  disabled: boolean;
  onFieldChange: <K extends keyof TemplateFormData>(
    key: K,
    value: TemplateFormData[K],
  ) => void;
};

export function TemplateDetailSettingsSection({
  dict,
  fieldLabels,
  policies,
  onPolicyChange,
  canEditTemplate,
  policyLabels,
  memberValueProps,
  accentCtaLabel,
  watchedForm,
  watchedLanguage,
  watchedVisibility,
  languageError,
  disabled,
  onFieldChange,
}: TemplateDetailSettingsSectionProps) {
  // DEV-507: hiding the up2promo branding and a custom accent need
  // `feature.profile_branding` (same rule as the profile editor).
  const { isFeatureEnabled, isLoading: entitlementsLoading } =
    useEntitlements();
  const canUseProfileBranding = isFeatureEnabled('feature.profile_branding');
  const brandingLocked = !entitlementsLoading && !canUseProfileBranding;
  const showUp2promoBranding = brandingLocked
    ? true
    : (watchedForm.showUp2promoBranding ?? true);

  return (
    <div className="flex w-full min-w-0 flex-col gap-4">
      <TemplateDetailSectionCard contentClassName="gap-4">
        <div className="flex flex-col gap-1">
          <h2 className={TEMPLATE_DETAIL_SECTION_TITLE_CLASS}>{dict.title}</h2>
          <p className={TEMPLATE_DETAIL_MUTED_CLASS}>{dict.description}</p>
        </div>
        <div className="flex min-w-0 flex-col gap-3">
          <TemplateFieldRow
            fieldKey="language"
            policies={policies}
            onPolicyChange={onPolicyChange}
            canManage={canEditTemplate}
            policyLabels={policyLabels}
            memberValueColumn={{
              kind: 'setting',
              key: 'language',
              label: fieldLabels.language,
              inputType: 'enum',
              options: [
                { value: 'auto', label: dict.languageAuto },
                { value: 'de', label: dict.languageGerman },
                { value: 'en', label: dict.languageEnglish },
              ],
            }}
            memberDialogTitle={fieldLabels.language}
            memberDefaultValue={watchedLanguage}
            {...memberValueProps}
            label={<Label htmlFor="language">{dict.language}</Label>}
          >
            <Select
              value={watchedLanguage}
              disabled={disabled}
              onValueChange={(value) => {
                const language = value as 'auto' | 'de' | 'en';
                onFieldChange('language', language);
              }}
            >
              <SelectTrigger id="language" className="min-w-0 w-full">
                <SelectValue placeholder={dict.languagePlaceholder} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">{dict.languageAuto}</SelectItem>
                <SelectItem value="de">{dict.languageGerman}</SelectItem>
                <SelectItem value="en">{dict.languageEnglish}</SelectItem>
              </SelectContent>
            </Select>
            {languageError ? (
              <p className="text-[13.5px] text-destructive">
                {languageError.message}
              </p>
            ) : null}
          </TemplateFieldRow>

          <TemplateFieldRow
            fieldKey="visibility"
            policies={policies}
            onPolicyChange={onPolicyChange}
            canManage={canEditTemplate}
            policyLabels={policyLabels}
            memberValueColumn={{
              kind: 'setting',
              key: 'visibility',
              label: fieldLabels.visibility,
              inputType: 'enum',
              options: [
                {
                  value: 'unlisted',
                  label: dict.visibilityUnlisted,
                },
                { value: 'private', label: dict.visibilityPrivate },
              ],
            }}
            memberDialogTitle={fieldLabels.visibility}
            memberDefaultValue={watchedVisibility}
            {...memberValueProps}
            label={<Label htmlFor="visibility">{dict.visibility}</Label>}
          >
            <Select
              value={watchedVisibility}
              disabled={disabled}
              onValueChange={(value) => {
                const visibility = value as 'private' | 'unlisted';
                onFieldChange('visibility', visibility);
              }}
            >
              <SelectTrigger id="visibility" className="min-w-0 w-full">
                <SelectValue placeholder={dict.visibilityPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unlisted">
                  {dict.visibilityUnlisted}
                </SelectItem>
                <SelectItem value="private">
                  {dict.visibilityPrivate}
                </SelectItem>
              </SelectContent>
            </Select>
          </TemplateFieldRow>

          <TemplateFieldRow
            fieldKey="showUp2promoBranding"
            policies={policies}
            onPolicyChange={onPolicyChange}
            canManage={canEditTemplate}
            policyLabels={policyLabels}
            memberValueColumn={{
              kind: 'setting',
              key: 'showUp2promoBranding',
              label: fieldLabels.showUp2promoBranding,
              inputType: 'boolean',
            }}
            memberDialogTitle={fieldLabels.showUp2promoBranding}
            memberDefaultValue={
              (watchedForm.showUp2promoBranding ?? true) ? 'true' : 'false'
            }
            {...memberValueProps}
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-col gap-0.5">
                <Label htmlFor="showUp2promoBranding">
                  {dict.showUp2promoBranding}
                </Label>
                <p className={TEMPLATE_DETAIL_MUTED_CLASS}>
                  {dict.showUp2promoBrandingDescription}
                </p>
              </div>
              <Switch
                id="showUp2promoBranding"
                checked={showUp2promoBranding}
                disabled={disabled || entitlementsLoading || brandingLocked}
                onCheckedChange={(checked) => {
                  onFieldChange('showUp2promoBranding', checked);
                }}
              />
            </div>
            {brandingLocked ? (
              <UpgradeCard
                feature="feature.profile_branding"
                compact
                title={dict.showUp2promoBrandingUpgradeTitle}
                description={dict.showUp2promoBrandingUpgradeDescription}
              />
            ) : null}
          </TemplateFieldRow>
        </div>
      </TemplateDetailSectionCard>

      <ProfileAppearanceFields
        dict={dict.appearance}
        disabled={disabled}
        values={{
          layout: watchedForm.layout ?? PROFILE_STYLING_FORM_DEFAULTS.layout,
          theme: watchedForm.theme ?? PROFILE_STYLING_FORM_DEFAULTS.theme,
          headerStyle:
            watchedForm.headerStyle ??
            PROFILE_STYLING_FORM_DEFAULTS.headerStyle,
          headerImage: watchedForm.headerImage ?? null,
          buttonShape: watchedForm.buttonShape ?? null,
        }}
        onChange={(key, value) => onFieldChange(key, value as never)}
      />

      <TemplateDetailSectionCard contentClassName="gap-4">
        <div className="flex min-w-0 flex-col gap-3">
          <TemplateFieldRow
            fieldKey="accentColor"
            policies={policies}
            onPolicyChange={onPolicyChange}
            canManage={canEditTemplate}
            policyLabels={policyLabels}
            label={
              <div className="flex flex-col gap-0.5">
                <Label>{dict.accentColor}</Label>
                <p className={TEMPLATE_DETAIL_MUTED_CLASS}>
                  {dict.accentColorDescription}
                </p>
              </div>
            }
          >
            <ProfileAccentColorField
              value={brandingLocked ? null : watchedForm.accentColor}
              disabled={disabled || entitlementsLoading || brandingLocked}
              showHeader={false}
              labels={{
                label: dict.accentColor,
                description: dict.accentColorDescription,
                reset: dict.accentColorReset,
                placeholder: dict.accentColorPlaceholder,
                ctaPreview: {
                  ctaLabel: accentCtaLabel,
                  preview: dict.accentColorCtaPreview,
                  adjustedDarker: dict.accentColorCtaAdjustedDarker,
                  adjustedLighter: dict.accentColorCtaAdjustedLighter,
                },
              }}
              onChange={(next) => {
                onFieldChange('accentColor', next);
              }}
            />
          </TemplateFieldRow>
        </div>
      </TemplateDetailSectionCard>
    </div>
  );
}
